import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, firstValueFrom, of } from "rxjs";
import { ApiService } from "../core/api.service";
import { ConfirmDialogService } from "../core/confirm-dialog.service";
import { BlindCategory } from "../core/models";

@Component({
  selector: "app-category-list-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule],
  template: `
    <section class="dashboard">
      <div class="dashboard-main">
        <header class="dashboard-header">
          <div>
            <h1>Categories</h1>
            <p class="header-subtitle">Group blinds by product family.</p>
          </div>
          <div class="header-actions">
            <a class="action-button" routerLink="/catalog">← Back</a>
          </div>
        </header>

        <div class="panel-card">
          <div class="panel-header">
            <h2>Add category</h2>
          </div>
          <div class="panel-content">
            <div class="category-add">
              <input
                class="form-input"
                [(ngModel)]="newName"
                placeholder="e.g. Roller"
                (keyup.enter)="addCategory()"
              />
              <button class="action-button primary" (click)="addCategory()" [disabled]="saving()">
                Add
              </button>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <h2>All categories</h2>
            <p class="panel-description">{{ categories()?.length ?? 0 }} total</p>
          </div>
          <div class="panel-content">
            @if (categories()?.length === 0) {
              <p class="empty-message">No categories yet. Add one above.</p>
            }
            @for (cat of categories(); track cat.id) {
              @if (editingId() === cat.id) {
                <div class="category-edit-row">
                  <input
                    class="form-input"
                    [(ngModel)]="editName"
                    placeholder="Category name"
                    (keyup.enter)="saveEdit()"
                  />
                  <button class="action-button primary" (click)="saveEdit()" [disabled]="saving()">Save</button>
                  <button class="action-button" (click)="cancelEdit()">Cancel</button>
                </div>
              } @else {
                <div class="category-row">
                  <div class="category-main">
                    <div class="category-thumb">
                      @if (cat.imageUrl) {
                        <img [src]="cat.imageUrl" [alt]="cat.name" />
                      } @else {
                        <span class="category-thumb-placeholder">No image</span>
                      }
                    </div>
                    <span class="category-name">{{ cat.name }}</span>
                  </div>
                  <div class="row-actions">
                    <label
                      class="chip-btn"
                      [class.disabled]="uploadingId() === cat.id"
                      [title]="cat.imageUrl ? 'Replace image' : 'Upload image'"
                    >
                      {{ uploadingId() === cat.id ? "Uploading…" : "Image" }}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        [disabled]="uploadingId() === cat.id"
                        (change)="uploadCategoryImage(cat, $event)"
                      />
                    </label>
                    @if (cat.imageUrl) {
                      <button
                        class="chip-btn danger"
                        (click)="clearCategoryImage(cat)"
                        [disabled]="uploadingId() === cat.id"
                        title="Remove image"
                      >
                        Remove image
                      </button>
                    }
                    <button class="chip-btn" (click)="startEdit(cat)" title="Edit">✎</button>
                    <button class="chip-btn danger" (click)="deleteCategory(cat)" title="Delete">×</button>
                  </div>
                </div>
              }
            }
            @if (message()) {
              <div class="status-message">{{ message() }}</div>
            }
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      .dashboard { display: grid; grid-template-columns: 1fr; gap: 24px; align-items: start; }
      .dashboard-main { min-width: 0; }
      .dashboard-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; }
      .dashboard-header h1 { font-size: 32px; margin: 0; }
      .header-subtitle { color: var(--ink-500); margin: 4px 0 0; font-size: 14px; }
      .header-actions { display: flex; gap: 10px; align-items: center; }
      .action-button {
        border: none; background: var(--panel); color: var(--ink-900); padding: 10px 16px;
        border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer;
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08); text-decoration: none;
        display: inline-flex; align-items: center;
      }
      .action-button.primary { background: #111827; color: #ffffff; box-shadow: 0 8px 20px rgba(17, 24, 39, 0.15); }
      .action-button:disabled { opacity: 0.5; cursor: not-allowed; }
      .panel-card { background: var(--panel); border-radius: 20px; padding: 24px; box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1); margin-bottom: 24px; }
      .panel-header { margin-bottom: 18px; }
      .panel-header h2 { font-size: 18px; margin: 0; color: var(--ink-900); }
      .panel-description { font-size: 13px; color: var(--ink-500); margin: 4px 0 0; }
      .panel-content { display: grid; gap: 12px; }
      .category-add { display: flex; gap: 8px; }
      .category-add .form-input { flex: 1; }
      .form-input { padding: 10px 12px; border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 10px; font-size: 14px; font-family: inherit; color: var(--ink-900); background: #fafbfc; }
      .form-input:focus { outline: none; border-color: #111827; background: #ffffff; box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.08); }
      .category-row, .category-edit-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 0; border-bottom: 1px solid rgba(15, 23, 42, 0.08); flex-wrap: wrap; }
      .category-edit-row { justify-content: flex-start; }
      .category-edit-row .form-input { flex: 1; max-width: 320px; min-width: 0; }
      .category-name { font-weight: 600; font-size: 14px; color: var(--ink-900); }
      .category-main { display: flex; align-items: center; gap: 12px; min-width: 0; }
      .category-thumb { width: 48px; height: 48px; border-radius: 10px; overflow: hidden; background: #f1f5f9; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
      .category-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .category-thumb-placeholder { font-size: 10px; color: var(--ink-500); text-align: center; }
      .chip-btn.disabled { opacity: 0.5; pointer-events: none; }
      .row-actions { display: flex; gap: 4px; flex-wrap: wrap; }
      @media (max-width: 600px) {
        .category-add { flex-wrap: wrap; }
        .category-edit-row .form-input { max-width: none; }
      }
      .chip-btn { border: none; background: transparent; cursor: pointer; color: var(--ink-500); font-size: 13px; padding: 2px 4px; }
      .chip-btn.danger { color: #dc2626; }
      .chip-btn:hover { background: rgba(15, 23, 42, 0.06); border-radius: 6px; }
      .empty-message { color: var(--ink-500); font-size: 13px; }
      .status-message { color: var(--ink-500); font-size: 13px; padding: 12px; background: #fef3c7; border-radius: 10px; margin-top: 12px; }
    `,
  ],
})
export class CategoryListPageComponent {
  private readonly api = inject(ApiService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly saving = signal(false);
  readonly message = signal("");
  readonly uploadingId = signal<string | null>(null);
  newName = "";
  editName = "";
  readonly editingId = signal<string | null>(null);

  readonly categoriesResource = rxResource({
    stream: () =>
      this.api.getCategories().pipe(catchError(() => of([] as BlindCategory[]))),
  });
  readonly categories = this.categoriesResource.value;

  addCategory() {
    const name = this.newName.trim();
    if (!name) {
      this.message.set("Category name is required.");
      return;
    }
    this.saving.set(true);
    this.api.createCategory(name).subscribe({
      next: (created) => {
        this.categoriesResource.update((list) =>
          [...(list ?? []), created].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          ),
        );
        this.newName = "";
        this.saving.set(false);
        this.message.set("Category added.");
      },
      error: (err) => {
        this.message.set(err?.error?.message ?? "Failed to add category.");
        this.saving.set(false);
      },
    });
  }

  startEdit(cat: BlindCategory) {
    this.editingId.set(cat.id);
    this.editName = cat.name;
  }

  cancelEdit() {
    this.editingId.set(null);
  }

  saveEdit() {
    const id = this.editingId();
    const name = this.editName.trim();
    if (!id || !name) {
      this.message.set("Category name is required.");
      return;
    }
    this.saving.set(true);
    this.api.updateCategory(id, { name }).subscribe({
      next: (updated) => {
        this.categoriesResource.update((list) =>
          (list ?? []).map((c) => (c.id === updated.id ? updated : c)),
        );
        this.editingId.set(null);
        this.saving.set(false);
        this.message.set("Category updated.");
      },
      error: (err) => {
        this.message.set(err?.error?.message ?? "Failed to update category.");
        this.saving.set(false);
      },
    });
  }

  async uploadCategoryImage(cat: BlindCategory, event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      this.message.set("Please choose an image file.");
      return;
    }
    this.uploadingId.set(cat.id);
    try {
      const presigned = await firstValueFrom(
        this.api.presignCatalogImage(file.type || "image/jpeg", file.name),
      );
      const resp = await fetch(presigned.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "image/jpeg" },
        body: file,
      });
      if (!resp.ok) throw new Error("Image upload failed");
      const updated = await firstValueFrom(
        this.api.updateCategory(cat.id, {
          imageKey: presigned.objectKey,
          imageUrl: presigned.publicUrl,
        }),
      );
      this.replaceCategory(updated);
      this.message.set("Category image saved.");
    } catch (err: any) {
      this.message.set(err?.message ?? "Failed to upload image.");
    } finally {
      this.uploadingId.set(null);
    }
  }

  clearCategoryImage(cat: BlindCategory) {
    this.uploadingId.set(cat.id);
    this.api.updateCategory(cat.id, { imageKey: "", imageUrl: "" }).subscribe({
      next: (updated) => {
        this.replaceCategory(updated);
        this.uploadingId.set(null);
        this.message.set("Category image removed.");
      },
      error: (err) => {
        this.message.set(err?.error?.message ?? "Failed to remove image.");
        this.uploadingId.set(null);
      },
    });
  }

  private replaceCategory(updated: BlindCategory) {
    this.categoriesResource.update((list) =>
      (list ?? []).map((c) => (c.id === updated.id ? updated : c)),
    );
  }

  async deleteCategory(cat: BlindCategory) {
    const confirmed = await this.confirmDialog.confirm({
      title: "Delete category",
      message: `Delete category "${cat.name}"?`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!confirmed) return;
    this.api.deleteCategory(cat.id).subscribe({
      next: () => {
        this.categoriesResource.update((list) =>
          (list ?? []).filter((c) => c.id !== cat.id),
        );
        this.message.set("Category deleted.");
      },
      error: (err) =>
        this.message.set(err?.error?.message ?? "Failed to delete category."),
    });
  }
}
